import React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { renderMarkdown } from "@impero-com/ui";
import { Typography } from "antd";
import type { TextAreaRef } from "antd/es/input/TextArea";

import { MarkdownInput } from "components/form/fields/markdownInput";
import {
    BaseFormField,
    EditInput,
    EditProps,
} from "components/form/legacyFields";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { MarkdownEditor } from "components/markdownEditor";
import {
    MarkdownEditorBlurEvent,
    MarkdownEditorMethods,
} from "components/markdownEditor/types";
import {
    CustomizedValidation,
    EditFormCreate,
    FormComponentProps,
    ValidationRule,
    validationRulesConverter,
} from "utils/form";

const { Text } = Typography;

interface CreateTextAreaProps<ID extends string, R>
    extends FormComponentProps<ID, string, R>, TextAreaCommonProps {
    id: ID;
    initialValue: string | undefined;
    validators?: ValidationRule<string | undefined>[];
    creationMode?: boolean;
    empty?: boolean;
    validationError?: CustomizedValidation;
}

interface CreateTextAreaState {
    editing: boolean;
}

class CreateTextArea<ID extends string, R> extends React.PureComponent<
    CreateTextAreaProps<ID, R>,
    CreateTextAreaState
> {
    private innerField = React.createRef<TextAreaRef>();
    private markdownEditorRef = React.createRef<MarkdownEditorMethods>();
    private focusIntervalId: number | undefined = undefined;

    state: CreateTextAreaState = {
        editing: false,
    };

    render = () => {
        const {
            form: { isFieldValidating, getFieldError },
            id,
            label,
            creationMode,
            validationError,
        } = this.props;
        const { editing } = this.state;
        const readonly =
            !creationMode &&
            !(editing || isFieldValidating(id) || getFieldError(id));

        return (
            <BaseFormField
                getComponent={this.getComponent}
                getReadonlyComponent={
                    !creationMode ? this.getReadonlyComponent : undefined
                }
                onClick={this.onLabelClick}
                hasFeedback={false}
                label={label}
                readonly={readonly}
                validationError={validationError}
            />
        );
    };

    getComponent = () => {
        const { form, id, validators, label, initialValue } = this.props;

        const { getFieldDecorator } = form;

        // If type = "CREATE" (creationMode = true) we use MarkdownInput.
        // MarkdownInput is a wrapper around MarkdownEditor, which requires a user to click on the content to get into the editing mode.
        // That was an explicit requirement from the product (Xisi).
        //
        // If type = "EDIT" (creationMode = false) we use MarkdownEditor directly
        // In order to get into editing mode the user needs to click on the pencil icon.
        // In that case we use MarkdownEditor (and not MarkdownInput) in order not to force the user to click twice.
        if (this.props.creationMode) {
            return getFieldDecorator(id, {
                rules: validationRulesConverter<string>()(
                    id,
                    form,
                    validators,
                    label
                ),
                valuePropName: "value",
                trigger: "onBlur",
                validateFirst: true,
                preserve: true,
                initialValue,
                getValueFromEvent: (e: MarkdownEditorBlurEvent) =>
                    e.markdownContent,
            })(
                <MarkdownInput
                    ref={this.markdownEditorRef}
                    onBlur={this.onBlur}
                    onFocus={this.onFocus}
                />
            );
        } else {
            return getFieldDecorator(id, {
                rules: validationRulesConverter<string>()(
                    id,
                    form,
                    validators,
                    label
                ),
                valuePropName: "initialContent",
                trigger: "onBlur",
                validateFirst: true,
                preserve: true,
                initialValue,
                getValueFromEvent: (e: MarkdownEditorBlurEvent) =>
                    e.markdownContent,
            })(
                <MarkdownEditor
                    ref={this.markdownEditorRef}
                    onBlur={this.onBlur}
                    onFocus={this.onFocus}
                />
            );
        }
    };

    getReadonlyComponent = () => {
        const {
            form: { getFieldValue },
            id,
            empty,
        } = this.props;
        const value = getFieldValue(id) ?? "";
        return (
            <Text type={empty ? "secondary" : undefined}>
                {empty ? t("Form_No_Field_Set") : renderMarkdown(value)}
            </Text>
        );
    };

    onLabelClick = () => {
        if (!this.state.editing) {
            this.setState({ editing: true }, this.giveFocus);
        }
    };

    onFocus = () => {
        if (!this.state.editing) {
            this.setState({ editing: true });
        }
    };

    onBlur = () => {
        if (this.state.editing) {
            this.setState({ editing: false });
        }
    };

    giveFocus = () => {
        // HACK: By some reason, because MarkdownEditor is lazy loaded, at the moment when `giveFocus()` is called,
        // `this.markdownEditorRef.current` is still `null`. So, we need to wait a bit before focusing.
        window.setTimeout(() => {
            if (this.markdownEditorRef.current) {
                this.markdownEditorRef.current.focus();
            }
        }, 50);
    };

    componentWillUnmount = () => {
        if (this.focusIntervalId) {
            clearInterval(this.focusIntervalId);
        }
    };

    componentDidMount = () => {
        // Preload MarkdownEditor component, so it can be instantly rendered if the user
        // switched to the editing mode.
        MarkdownEditor.preload();
    };
}

interface EditTextAreaProps
    extends EditProps<string | undefined>, TextAreaCommonProps {
    empty?: boolean;
}

const EditTextAreaForm = EditFormCreate<string, EditTextAreaProps>()(
    CreateTextArea
);

const EditTextArea = EditInput<string, EditTextAreaProps>(EditTextAreaForm);

interface TextAreaCommonProps {
    label: string;
}

type TextAreaProps<ID extends string, R> =
    | ({ type: "READ_ONLY" } & TextAreaCommonProps & {
              value: string | undefined;
          })
    | ({ type: "EDIT" } & EditTextAreaProps)
    | ({ type: "CREATE" } & CreateTextAreaProps<ID, R>);

class TextAreaInput<ID extends string, R> extends React.PureComponent<
    TextAreaProps<ID, R>
> {
    render = () => {
        const { label } = this.props;
        switch (this.props.type) {
            case "READ_ONLY": {
                const { value } = this.props;
                const valueIdentifier: string =
                    value === undefined ? "" : value;
                const displayValue: string =
                    valueIdentifier === ""
                        ? t("Form_No_Field_Set")
                        : valueIdentifier;
                return (
                    <BaseFormDisplay label={label}>
                        <Text
                            type={
                                valueIdentifier === "" ? "secondary" : undefined
                            }
                        >
                            {renderMarkdown(displayValue)}
                        </Text>
                    </BaseFormDisplay>
                );
            }
            case "EDIT": {
                const { value, type: _type, ...rest } = this.props;
                return (
                    <EditTextArea
                        {...rest}
                        value={value}
                        empty={value === undefined || value === ""}
                    />
                );
            }
            case "CREATE": {
                const { type: _type, ...rest } = this.props;
                return <CreateTextArea creationMode {...rest} />;
            }
            default:
                return assertNever(this.props);
        }
    };
}
export type {
    TextAreaProps,
    TextAreaCommonProps,
    EditTextAreaProps,
    CreateTextAreaProps,
};
export { TextAreaInput };
